/**
 * A composite symbol — `AAPL/SPY`, `A+B/C`, `AAPL-MSFT`.
 *
 * The client half of a grammar `services/qv-data/app/qf/composite.py` also
 * implements. Neither is written against the other: both are written against
 * `contract/examples/composite-symbols.json`, which is the contract, and both
 * test suites read it. Two implementations of one grammar is the risk this
 * feature carries, and the fixture is what makes it a *checked* risk.
 *
 * ## A module rather than a helper inside the dialog
 *
 * The dialog needs `activeToken` and the operand rewrite. But five other
 * surfaces need to ask "is this a composite" for reasons of their own — which
 * market's currency to format in, whether a company profile exists, whether a
 * trading plan may be written — and none of them should be re-deciding what an
 * operator is.
 *
 * ## Parsing is pure; routing asks a question
 *
 * `parseComposite` never consults anything. `isComposite` does, and only for
 * one case: a string whose sole operator is `-`, because `BRK-B` and
 * `BAJAJ-AUTO.NS` are real tickers and `AAPL-MSFT` is a spread. Here the
 * resolver is the search index; on the server it is a bar file. With no
 * resolver the answer is `false`, so a hyphenated ticker keeps charting rather
 * than being torn into two legs that do not exist.
 *
 * ## Why an expression carries no spaces
 *
 * `SymbolSearch`'s commit cuts at the first whitespace — the last defence
 * against `"AAPL - Apple Inc."` reaching `ChartGroup.symbol`. A spaced spelling
 * would be truncated before it ever became a request, so the canonical form has
 * none and `formatComposite` exists to add them back for display only.
 */

/** Every operator in the grammar. */
const OPERATORS = "+-*/";

/**
 * The three that settle routing on sight. `-` is absent — see the module note.
 */
const DECISIVE = "+*/";

/**
 * What an operand may be spelled with. **No hyphen** (it always splits) and no
 * separator, so a traversal segment cannot survive tokenising.
 *
 * `^` because 333 index files begin with one, `&` because `M&M.NS` is real, and
 * `.` because it is structural in India and in every US class share.
 */
const OPERAND_CHARS = /^[A-Z0-9.&^]+$/;

/** Matches the server's `symbol VARCHAR(20)`. */
const MAX_OPERAND = 20;

/**
 * The one spelling the universe and a saved workspace use: `-` becomes `.`.
 *
 * Mirrors `market.canonical_symbol` on the server, and exists for the same
 * reason: **`-` is reserved for arithmetic**, so a ticker carrying one would
 * make `A-B` ambiguous forever. `BRK-B` is `BRK.B` and `BAJAJ-AUTO.NS` is
 * `BAJAJ.AUTO.NS` — the market suffix survives untouched, because no suffix
 * contains a hyphen.
 */
export function canonicalSymbol(symbol: string): string {
  return symbol.toUpperCase().replace(/-/g, ".");
}

/**
 * A chart's symbol as a list or the clipboard should hold it: upper case, no
 * spaces. For a ratio that is the canonical composite — exactly what
 * `SymbolDialog`'s commit writes into `ChartGroup.symbol` and what the chart's
 * star files — and for a ticker it is the ticker. Copy symbol and the star use
 * one spelling, so a copied ratio pastes back as the same row.
 */
export function canonicalComposite(symbol: string): string {
  return symbol.replace(/\s+/g, "").toUpperCase();
}

/**
 * What a typed symbol commits as — `SymbolDialog`'s rule, shared with the
 * command search's Go to so the two cannot chart one ratio under two spellings.
 *
 * **An expression has its whitespace removed and is uppercased**, because the
 * canonical form has neither: `SymbolSearch`'s own commit cuts a value at the
 * first space, so `AAPL / SPY` reaching `ChartGroup.symbol` would be truncated
 * to `AAPL` the next time that box committed. A plain ticker is only trimmed,
 * leaving the single-symbol path exactly as it was.
 */
export function committedSymbol(text: string): string {
  const trimmed = text.trim();
  return /[+\-*/]/.test(trimmed) ? trimmed.replace(/\s+/g, "").toUpperCase() : trimmed;
}

export type CompositeErrorKind ="empty" | "operand" | "character" | "length";

export interface CompositeNode {
  /** A leaf. Exactly one of `sym` and `op` is present. */
  sym?: string;
  op?: string;
  left?: CompositeNode;
  right?: CompositeNode;
}

export interface ParsedComposite {
  /** The tree, for anything that needs the structure. */
  ast: CompositeNode;
  /** Distinct operands, in first-appearance order. */
  operands: string[];
}

export class CompositeError extends Error {
  readonly kind: CompositeErrorKind;
  constructor(kind: CompositeErrorKind, message: string) {
    super(message);
    this.name = "CompositeError";
    this.kind = kind;
  }
}

function validateOperand(raw: string, expression: string): string {
  // Order matters, and it is the order a reader would check them in: something
  // rather than nothing, legal characters, short enough, then actually naming
  // something. `..` reaches the last check — legal characters and short — which
  // is why that check is more than a formality.
  if (!raw) throw new CompositeError("operand", `${expression}: an operator is missing an operand`);
  if (!OPERAND_CHARS.test(raw)) {
    throw new CompositeError("character", `${expression}: ${raw} is not spelled like a symbol`);
  }
  if (raw.length > MAX_OPERAND) {
    throw new CompositeError("length", `${expression}: ${raw} is longer than a symbol can be`);
  }
  if (!/[A-Z0-9]/.test(raw)) {
    throw new CompositeError("operand", `${expression}: ${raw} is not a symbol`);
  }
  return raw;
}

/**
 * `expression` as a tree. Pure — no index, no network, no configuration.
 *
 * Two levels of precedence, left-associative, no parentheses: `A+B/C` is
 * `A+(B/C)` and `A-B-C` is `(A-B)-C`. Whitespace is ignored so a pasted value
 * still parses, though the canonical form has none.
 *
 * Throws `CompositeError`; `parseComposite` is the non-throwing wrapper most
 * callers want.
 */
export function parseCompositeOrThrow(expression: string): ParsedComposite {
  const text = (expression ?? "").replace(/\s+/g, "").toUpperCase();
  if (!text) throw new CompositeError("empty", "an empty expression names no symbol");

  const operands: CompositeNode[] = [];
  const ops: string[] = [];
  let buffer = "";
  for (const ch of text) {
    if (OPERATORS.includes(ch)) {
      operands.push({ sym: validateOperand(buffer, expression) });
      ops.push(ch);
      buffer = "";
    } else {
      buffer += ch;
    }
  }
  operands.push({ sym: validateOperand(buffer, expression) });

  // Fold `*` and `/` as they are met and collect `+`/`-` for a second pass. Two
  // passes rather than precedence climbing: there are exactly two levels and no
  // parentheses, so the general machinery would outweigh the grammar.
  const terms: CompositeNode[] = [operands[0]!];
  const termOps: string[] = [];
  ops.forEach((op, i) => {
    const right = operands[i + 1]!;
    if (op === "*" || op === "/") {
      terms[terms.length - 1] = { op, left: terms[terms.length - 1]!, right };
    } else {
      termOps.push(op);
      terms.push(right);
    }
  });

  let ast = terms[0]!;
  termOps.forEach((op, i) => {
    ast = { op, left: ast, right: terms[i + 1]! };
  });

  return { ast, operands: symbolsOf(ast) };
}

/** Every distinct operand, left to right, first appearance kept. */
function symbolsOf(node: CompositeNode): string[] {
  const seen: string[] = [];
  const stack: CompositeNode[] = [node];
  while (stack.length) {
    const current = stack.pop()!;
    if (current.sym !== undefined) {
      if (!seen.includes(current.sym)) seen.push(current.sym);
    } else {
      // Right then left, so the pops come out left to right — which is what
      // makes "first appearance" mean what a reader of the expression expects.
      stack.push(current.right!);
      stack.push(current.left!);
    }
  }
  return seen;
}

/** `parseCompositeOrThrow`, answering `null` instead of throwing. */
export function parseComposite(expression: string): ParsedComposite | null {
  try {
    return parseCompositeOrThrow(expression);
  } catch {
    return null;
  }
}

/**
 * Whether `symbol` should be read as an expression rather than as one ticker.
 *
 * `holds` answers "is this a symbol we know", and is consulted **only** when the
 * sole operator is `-`. Pass the search index; omit it and a hyphen is assumed
 * to be part of the ticker, which is the safe default — `BRK-B` keeps charting.
 */
export function isComposite(
  symbol: string | null | undefined,
  holds?: (symbol: string) => boolean,
): boolean {
  const text = (symbol ?? "").trim().toUpperCase();
  if (!text) return false;
  if ([...text].some((ch) => DECISIVE.includes(ch))) return true;
  if (!text.includes("-")) return false;
  if (!holds) return false;
  return !holds(text);
}

/**
 * The first operand — whose market, currency and volume the composite takes.
 *
 * **Routes before it parses**, and that is not a nicety: `BAJAJ-AUTO.NS` parses
 * happily as `BAJAJ - AUTO.NS`, so a blind parse would answer `BAJAJ`, and
 * `marketForSymbol` would then format an Indian ticker in dollars. Pass `holds`
 * wherever the index is available; without it a hyphenated string is treated as
 * one ticker, which is the safe half of the trade.
 *
 * Falls back to the string itself for anything that will not parse, so callers
 * that only want "something to format against" never have to null-check.
 */
export function primarySymbol(symbol: string, holds?: (symbol: string) => boolean): string {
  if (!isComposite(symbol, holds)) return symbol;
  return parseComposite(symbol)?.operands[0] ?? symbol;
}

/**
 * `AAPL/SPY` as `AAPL / SPY` — **display only**.
 *
 * Routes first, for the reason `primarySymbol` does: spacing the hyphen in
 * `BRK-B` would render a real ticker as `BRK - B` in the toolbar box.
 *
 * Never commit the result back — the spaced form is truncated at the first
 * space by `SymbolSearch`'s commit rule, so a round trip would leave a chart
 * showing `AAPL`.
 */
export function formatComposite(symbol: string, holds?: (symbol: string) => boolean): string {
  if (!isComposite(symbol, holds)) return symbol;
  return symbol.replace(/\s+/g, "").replace(/([+\-*/])/g, " $1 ");
}

export interface ActiveToken {
  start: number;
  end: number;
  value: string;
}

/**
 * The operand the caret sits in — what the search list should be ranking on.
 *
 * Typing `AAPL/SP` must list SPY, not nothing, so the dialog filters on this
 * rather than on the whole query. The reference implementation
 * (`SearchSymbol.xaml.cs`'s `TextEdit_EditValueChanged`) does the same, and it
 * is the behaviour that makes the operator buttons usable at all.
 *
 * A caret **on a boundary belongs to the token on its left**, which is the one
 * just typed into. At position 0 there is nothing on the left, so the token to
 * the right is taken instead.
 */
export function activeToken(text: string, caret: number): ActiveToken {
  const clamped = Math.max(0, Math.min(caret, text.length));
  let start = clamped;
  while (start > 0 && !OPERATORS.includes(text[start - 1]!)) start -= 1;
  let end = clamped;
  while (end < text.length && !OPERATORS.includes(text[end]!)) end += 1;
  return { start, end, value: text.slice(start, end) };
}

/**
 * `text` with the caret's token replaced by `symbol`, and where the caret lands.
 *
 * **The inserted operand is dot-spelled when it joins an expression** — `BRK-B`
 * goes in as `BRK.B`. That is the whole reason `-` can be an operator with
 * nothing to disambiguate, and it is QuantVitals' ingest-time rule
 * (`str.replace_all("-", ".")`) applied at the one point where it costs nothing.
 *
 * A single-token query is left alone, so committing `BRK-B` on its own still
 * commits FMP's own spelling and charts exactly as it does today.
 */
export function replaceActiveToken(
  text: string,
  caret: number,
  symbol: string,
): { text: string; caret: number } {
  const token = activeToken(text, caret);
  const hasOperator = [...text].some((ch) => OPERATORS.includes(ch));
  const inserted = hasOperator ? canonicalSymbol(symbol) : symbol;
  const next = text.slice(0, token.start) + inserted + text.slice(token.end);
  return { text: next, caret: token.start + inserted.length };
}
